import {
  AppleProviderResponse,
  FacebookLoginResponse,
  GoogleLoginResponseOnline,
  SocialLogin,
} from '@capgo/capacitor-social-login';
import Logger from 'utils/Logger';
import { isAndroidApp, isCordova, isIosApp } from 'utils/platform';

export type SocialProvider = 'google' | 'facebook' | 'apple';

export interface NativeSocialAuthResult {
  success: boolean;
  provider: SocialProvider;
  idToken?: string;
  accessToken?: string;
  profile?: {
    email: string | null;
    familyName: string | null;
    givenName: string | null;
    id: string | null;
    name: string | null;
    imageUrl?: string | null;
  };
  error?: string;
  cancelled?: boolean;
}

let isInitialized = false;

export const initializeSocialLogin = async (): Promise<void> => {
  if (isInitialized || !isCordova()) {
    return;
  }

  try {
    // @todo: Apple Sign-In on Android requires a backend redirect URL which is not implemented yet
    await SocialLogin.initialize({
      google: {
        webClientId: GOOGLE_WEB_CLIENT_ID,
        iOSClientId: GOOGLE_IOS_CLIENT_ID,
        iOSServerClientId: GOOGLE_WEB_CLIENT_ID,
      },
      facebook: {
        appId: FB_APP_ID,
        clientToken: FB_CLIENT_TOKEN,
      },
      ...(isIosApp() && {
        apple: {
          redirectUrl: '',
        },
      }),
    });
    isInitialized = true;
    Logger.log('[NativeSocialAuth] Initialized successfully');
  } catch (error) {
    console.error('[NativeSocialAuth] Initialization failed:', error);
    throw error;
  }
};

const signInWithGoogle = async (): Promise<NativeSocialAuthResult> => {
  const loginResult = await SocialLogin.login({
    provider: 'google',
    options: {
      forcePrompt: true,
      filterByAuthorizedAccounts: false,
      autoSelectEnabled: false,
      scopes: ['email', 'profile'],
    },
  });

  if (loginResult.provider !== 'google') {
    return {
      success: false,
      provider: 'google',
      error: 'Unexpected provider response',
    };
  }

  const result = loginResult.result;

  if (result.responseType !== 'online') {
    return {
      success: false,
      provider: 'google',
      error: 'No ID token received from Google',
    };
  }

  const onlineResult = result as GoogleLoginResponseOnline;

  if (!onlineResult.idToken) {
    return {
      success: false,
      provider: 'google',
      error: 'No ID token received from Google',
    };
  }

  return {
    success: true,
    provider: 'google',
    idToken: onlineResult.idToken,
    accessToken: onlineResult.accessToken?.token,
    profile: {
      email: onlineResult.profile.email,
      familyName: onlineResult.profile.familyName,
      givenName: onlineResult.profile.givenName,
      id: onlineResult.profile.id,
      name: onlineResult.profile.name,
      imageUrl: onlineResult.profile.imageUrl,
    },
  };
};

const signInWithFacebook = async (): Promise<NativeSocialAuthResult> => {
  const loginResult = await SocialLogin.login({
    provider: 'facebook',
    options: {
      permissions: ['email', 'public_profile'],
      limitedLogin: false,
    },
  });

  if (loginResult.provider !== 'facebook') {
    return {
      success: false,
      provider: 'facebook',
      error: 'Unexpected provider response',
    };
  }

  const result = loginResult.result as FacebookLoginResponse;

  if (!result.accessToken?.token && !result.idToken) {
    return {
      success: false,
      provider: 'facebook',
      error: 'No access token received from Facebook',
    };
  }

  return {
    success: true,
    provider: 'facebook',
    idToken: result.idToken || undefined,
    accessToken: result.accessToken?.token,
    profile: {
      email: result.profile.email,
      familyName: null, // Facebook doesn't provide separate name fields
      givenName: null,
      id: result.profile.userID,
      name: result.profile.name,
      imageUrl: result.profile.imageURL,
    },
  };
};

const signInWithApple = async (): Promise<NativeSocialAuthResult> => {
  const loginResult = await SocialLogin.login({
    provider: 'apple',
    options: {
      scopes: ['email', 'name'],
    },
  });

  if (loginResult.provider !== 'apple') {
    return {
      success: false,
      provider: 'apple',
      error: 'Unexpected provider response',
    };
  }

  const result = loginResult.result as AppleProviderResponse;

  if (!result.idToken) {
    return {
      success: false,
      provider: 'apple',
      error: 'No ID token received from Apple',
    };
  }

  return {
    success: true,
    provider: 'apple',
    idToken: result.idToken,
    accessToken: result.accessToken?.token,
    profile: {
      email: result.profile.email,
      familyName: result.profile.familyName,
      givenName: result.profile.givenName,
      id: result.profile.user,
      name:
        result.profile.givenName && result.profile.familyName
          ? `${result.profile.givenName} ${result.profile.familyName}`
          : null,
    },
  };
};

const handleAuthError = (
  error: any,
  provider: SocialProvider,
): NativeSocialAuthResult => {
  const errorMessage = error?.message || error?.toString() || '';

  const cancelledCodes = ['popup_closed_by_user', 'USER_CANCELLED'];
  const cancelledPatterns = ['cancel', 'authorizationerror error 1001'];
  const isCancelledErrorCode = cancelledCodes.includes(error?.code);
  const isCancelledErrorPattern = cancelledPatterns.some((pattern) =>
    errorMessage.toLowerCase().includes(pattern),
  );
  const isCancelled = isCancelledErrorCode || isCancelledErrorPattern;

  if (isCancelled) {
    Logger.log(`[NativeSocialAuth] User cancelled ${provider} sign-in`);
    return {
      success: false,
      provider,
      cancelled: true,
      error: 'User cancelled',
    };
  }

  console.error(`[NativeSocialAuth] ${provider} sign-in error:`, error);
  return { success: false, provider, error: errorMessage || 'Unknown error' };
};

export const signInWithNative = async (
  provider: SocialProvider,
): Promise<NativeSocialAuthResult> => {
  try {
    await initializeSocialLogin();

    switch (provider) {
      case 'google':
        return await signInWithGoogle();
      case 'facebook':
        return await signInWithFacebook();
      case 'apple':
        return await signInWithApple();
      default:
        return {
          success: false,
          provider,
          error: `Unknown provider: ${provider}`,
        };
    }
  } catch (error: any) {
    return handleAuthError(error, provider);
  }
};

export const isNativeAuthAvailable = (provider: SocialProvider): boolean => {
  if (!isCordova()) {
    return false;
  }

  // do not allow Apple native sign-in on Android
  if (provider === 'apple') {
    return isIosApp();
  }

  return isAndroidApp() || isIosApp();
};
